import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import Notification from '../components/Notification';
import '../styles/verify-password.css'; // Reuse the same CSS file
import Cookies from 'js-cookie';

const VerifyUser = () => {
    const { userId } = useParams();
    const [code, setCode] = useState(new Array(6).fill(''));
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({
        message: '',
        type: 'info',
        header: ''
    });
    const [canResend, setCanResend] = useState(false);
    const [counter, setCounter] = useState(60);
    const [resendAttempts, setResendAttempts] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attemptsRemaining, setAttemptsRemaining] = useState(3);
    const [isLockedUntilTomorrow, setIsLockedUntilTomorrow] = useState(false);
    const inputRefs = useRef([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (counter > 0) {
            const timer = setTimeout(() => setCounter(counter - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [counter]);

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;
        const newCode = [...code];
        newCode[index] = element.value;
        setCode(newCode);

        // Focus next input
        if (element.value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const verificationCode = code.join('');
        setLoading(true);
        setIsSubmitting(true);

        try {
            const response = await fetch(`${config.backendUrl}/users/verify-sms-code/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code: verificationCode, user_id: userId }),
            });

            if (response.ok) {
                const data = await response.json();
                setNotification({
                    message: 'Numéro de téléphone vérifié avec succès! Redirection vers votre tableau de bord...',
                    type: 'success',
                    header: 'Bienvenue!'
                });

                // Store authentication data in both cookies and localStorage
                if (data.token) {
                    // Set token in cookies with 30-day expiry (persistent login)
                    Cookies.set('authToken', data.token, { expires: 30 });
                    localStorage.setItem('authToken', data.token);
                    localStorage.setItem('isLoggedIn', 'true');
                }
                
                if (data.user) {
                    localStorage.setItem('user', JSON.stringify(data.user));
                }

                // Clear pending user id
                localStorage.removeItem('pending_user_id');

                // Dispatch event to update user state across the app
                window.dispatchEvent(new Event('userStateChanged'));

                // Navigate to dashboard
                setTimeout(() => {
                    navigate('/dashboard');
                }, 1500);
            } else {
                setNotification({
                    message: 'Échec de la vérification. Le code peut être invalide ou expiré.',
                    type: 'error',
                    header: 'Erreur'
                });
                setIsSubmitting(false);
            }
        } catch (error) {
            setNotification({
                message: 'Une erreur est survenue. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur'
            });
            setIsSubmitting(false);
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: 'info', header: '' });
    };

    const handleResendCode = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${config.backendUrl}/users/send-verification-code/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: userId }),
            });

            const data = await response.json();

            if (response.ok) {
                setNotification({
                    message: `${data.message}. Il vous reste ${data.attempts_remaining} tentatives aujourd'hui.`,
                    type: 'success',
                    header: 'Succès'
                });
                setAttemptsRemaining(data.attempts_remaining);
                setCanResend(false);
                setCounter(60); // Reset the counter to 60 seconds
                setResendAttempts(prev => prev + 1); // Increment resend attempts
            } else {
                if (response.status === 400 && data.error.includes('limite de 3 tentatives')) {
                    setIsLockedUntilTomorrow(true);
                    setNotification({
                        message: data.error,
                        type: 'warning',
                        header: 'Limite atteinte'
                    });
                } else {
                    setNotification({
                        message: data.error || 'Échec de l\'envoi du code de vérification.',
                        type: 'error',
                        header: 'Erreur'
                    });
                }
                setResendAttempts(prev => prev + 1); // Increment attempts even on failure
            }
        } catch (error) {
            setNotification({
                message: 'Une erreur est survenue. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur'
            });
            setResendAttempts(prev => prev + 1); // Increment attempts on error
        } finally {
            setLoading(false);
        }
    };

    const isCodeComplete = code.every(digit => digit !== '');

    return (
        <div className="modern-auth-page" style={{ background: 'linear-gradient(135deg, #f8faff 0%, #e8f2ff 100%)' }}>
            {loading && <LoadingSpinner />}
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}
            <div className="verify-card" style={{ background: 'white', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                <h2 className="verify-title">Vérification du numéro de téléphone</h2>
                <p className="verify-instructions">
                    Un SMS contenant un code de vérification a été envoyé à votre numéro de téléphone.
                    Veuillez entrer ce code ci-dessous pour vérifier votre numéro. Merci!
                </p>
                <form className="verify-form" onSubmit={handleSubmit}>
                    <div className="verify-inputs">
                        {code.map((digit, index) => (
                            <input
                                key={index}
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleChange(e.target, index)}
                                onFocus={(e) => e.target.select()}
                                ref={(el) => (inputRefs.current[index] = el)}
                                className="verify-input"
                                disabled={isSubmitting}
                            />
                        ))}
                    </div>
                    <button
                        type="submit"
                        className="verify-submit"
                        disabled={!isCodeComplete || isSubmitting}
                    >
                        {isSubmitting ? 'Vérification...' : 'Vérifier'}
                    </button>
                </form>
                <div className="resend-container">
                    <p className="resend-text">Vous n'avez pas reçu le code ?</p>
                    <button
                        onClick={handleResendCode}
                        disabled={!canResend || isLockedUntilTomorrow || isSubmitting}
                        className={`resend-button ${(!canResend || isLockedUntilTomorrow || isSubmitting) ? 'disabled' : ''}`}
                    >
                        {isLockedUntilTomorrow
                            ? 'Réessayez demain'
                            : `Renvoyer le code ${canResend ? '' : `(${counter}s)`}`
                        }
                    </button>
                </div>
                
                {/* Only show WhatsApp contact after 2 or more resend attempts */}
                {resendAttempts >= 2 && (
                    <div className="whatsapp-contact-container">
                        <p className="whatsapp-text">
                            Toujours pas de code? 
                            <a 
                                href="https://wa.me/243851054526?text=Bonjour,%20je%20n'arrive%20pas%20à%20recevoir%20le%20code%20de%20vérification%20SMS%20sur%20Wennze.%20Pouvez-vous%20m'aider%20?"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-link"
                            >
                                Contactez-nous
                            </a>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyUser;